<template>
  <div>
    <!-- 设置可选择单个级别  :props="{ checkStrictly: true }"-->
    <el-cascader popper-class="china-area-cascader-popper" :props="{ checkStrictly: checkStrictly, multiple: multiple }" :options="options" v-model="selectedOptions" @change="handleChange"
                 :style="{ width: width }" />
  </div>
</template>

<script>
import cities from '@province-city-china/city'
import areas from '@province-city-china/area'
import towns from '@province-city-china/town'

const FUJIAN_PROVINCE_CODE = '350000'
const FUJIAN_PROVINCE_NAME = '福建省'
const FUJIAN_PROVINCE_2 = FUJIAN_PROVINCE_CODE.slice(0, 2) // 省级在该数据集中是 2 位

// @province-city-china/town 里每条记录的 code 是「所属区县」6 位码，同一区县下所有镇/街道共用同一个 code。
// 若把 town.code 当作 el-cascader 的 value，同级节点 value 全部相同，多选会错乱、重复，且名称映射互相覆盖。
function townLeafKey (countyCode, town) {
  return `${countyCode}_${String(town.town)}`
}

function buildFujianOptions () {
  const codeToName = {}

  // 仅用于 text <-> code 的互转
  const cityNameToCode = {}
  const countyNameToCodeByCity = {}
  const townNameToLeafKeyByCounty = {}
  const townLeafKeyToName = {}

  // province(2 位) -> city/area/town 三段编码（都在对应字段里）
  const fujianCities = cities.filter(c => c.province === FUJIAN_PROVINCE_2)

  const areasByCity = {}
  for (const area of areas) {
    if (area.province !== FUJIAN_PROVINCE_2) continue
    const cityKey = area.city // 2 位
    if (!areasByCity[cityKey]) areasByCity[cityKey] = []
    areasByCity[cityKey].push(area)
  }

  // key: `${city(2位)}-${area(2位)}`
  const townsByArea = {}
  for (const town of towns) {
    if (town.province !== FUJIAN_PROVINCE_2) continue
    const townKey = `${town.city}-${town.area}`
    if (!townsByArea[townKey]) townsByArea[townKey] = []
    townsByArea[townKey].push(town)
  }

  const options = fujianCities.map(city => {
    const cityCode = String(city.code)
    const cityName = city.name

    codeToName[cityCode] = cityName
    cityNameToCode[cityName] = cityCode

    const fujianCityAreas = areasByCity[city.city] || []
    countyNameToCodeByCity[cityCode] = {}

    return {
      value: cityCode,
      label: cityName,
      children: fujianCityAreas.map(area => {
        const countyCode = String(area.code)
        const countyName = area.name

        codeToName[countyCode] = countyName
        countyNameToCodeByCity[cityCode][countyName] = countyCode

        const areaKey = `${area.city}-${area.area}`
        const fujianTowns = townsByArea[areaKey] || []
        townNameToLeafKeyByCounty[countyCode] = {}

        return {
          value: countyCode,
          label: countyName,
          children: fujianTowns.map(town => {
            const leafKey = townLeafKey(countyCode, town)
            const townName = town.name

            townLeafKeyToName[leafKey] = townName
            townNameToLeafKeyByCounty[countyCode][townName] = leafKey

            return {
              value: leafKey,
              label: townName,
            }
          }),
        }
      }),
    }
  })

  return {
    options,
    codeToName,
    cityNameToCode,
    countyNameToCodeByCity,
    townNameToLeafKeyByCounty,
    townLeafKeyToName,
  }
}

const {
  options: FUJIAN_OPTIONS,
  codeToName: FUJIAN_CODE_TO_NAME,
  cityNameToCode: FUJIAN_CITY_NAME_TO_CODE,
  countyNameToCodeByCity: FUJIAN_COUNTY_NAME_TO_CODE_BY_CITY,
  townNameToLeafKeyByCounty: FUJIAN_TOWN_NAME_TO_LEAF_KEY_BY_COUNTY,
  townLeafKeyToName: FUJIAN_TOWN_LEAF_KEY_TO_NAME,
} = buildFujianOptions()

export default {
  props: {
    // 绑定值：
    // - 多选：传入字符串数组，如 ['福建省/福州市/仓山区/上街镇']
    // - 单选：传入字符串，如 '福建省/福州市/仓山区/上街镇'
    value: [String, Array, Object],
    // 是否多选
    multiple: { type: Boolean, default: false },
    // 是否可选择单级
    checkStrictly: { type: Boolean, default: false },
    // 组件宽度
    width: { type: String, default: '210px' },
  },
  watch: {
    value: {
      handler (val) {
        if (!val) return
        // 避免：用户在面板里勾选后，组件 emit 的 value 立刻回流触发 watcher，
        // 然后再用 textToCodePath 反解析覆盖 selectedOptions，导致“镇级勾选一闪就没/勾不上”
        // 交互触发时 el-cascader 已经把 selectedOptions 更新为 codePath 了，此处无需再回写。
        const lastEmittedKey = this._lastEmittedValueKey
        const incomingKey = (() => {
          try { return JSON.stringify(val) } catch (e) { return null }
        })()
        if (lastEmittedKey && incomingKey && lastEmittedKey === incomingKey) {
          return
        }
        if (typeof val === 'string') {
          this.selectedOptions = this.textToCodePath(val)
          return
        }
        if (Array.isArray(val)) {
          // 多选：value 进来是字符串数组
          this.selectedOptions = val.map(item => this.textToCodePath(item)).filter(Boolean)
          return
        }
        if (typeof val === 'object') {
          // 兼容旧传参：可能会传入数组包一层
          this.selectedOptions = Object.prototype.toString.call(val) === '[object Object]'
            ? []
            : val.map(item => this.textToCodePath(item)).filter(Boolean)
        }
      },
      deep: true,
      immediate: true,
    },
  },
  data () {
    return {
      // 展示三层：市 -> 县 -> 镇（省固定为福建）
      options: FUJIAN_OPTIONS,
      selectedOptions: [],
      _lastEmittedValueKey: null,
    };
  },

  methods: {
    codePathToText (codePath) {
      if (!Array.isArray(codePath) || codePath.length === 0) return null
      const cityCode = codePath[0]
      const cityName = FUJIAN_CODE_TO_NAME[cityCode]
      if (!cityName) return null

      if (codePath.length === 1) return `${FUJIAN_PROVINCE_NAME}/${cityName}`

      const countyCode = codePath[1]
      const countyName = FUJIAN_CODE_TO_NAME[countyCode]
      if (!countyName) return `${FUJIAN_PROVINCE_NAME}/${cityName}`

      if (codePath.length === 2) return `${FUJIAN_PROVINCE_NAME}/${cityName}/${countyName}`

      const townLeafKey = codePath[2]
      const townName = FUJIAN_TOWN_LEAF_KEY_TO_NAME[townLeafKey]
      if (!townName) return `${FUJIAN_PROVINCE_NAME}/${cityName}/${countyName}`

      return `${FUJIAN_PROVINCE_NAME}/${cityName}/${countyName}/${townName}`
    },

    textToCodePath (text) {
      if (!text || typeof text !== 'string') return null
      const parts = text.split('/').map(s => s.trim()).filter(Boolean)
      // 最少需要 city；当带“福建省”前缀时 parts 至少 2 段（省/市）
      if (parts.length < 1) return null

      // 省固定为福建省：允许传入带/不带“福建省”前缀
      const hasProvincePrefix = parts[0] === FUJIAN_PROVINCE_NAME
      const offset = hasProvincePrefix ? 1 : 0

      const cityName = parts[offset]
      const countyName = parts[offset + 1]
      const townName = parts[offset + 2]
      if (!cityName) return null

      const cityCode = FUJIAN_CITY_NAME_TO_CODE[cityName]
      if (!cityCode) return null

      // 仅选到“市”（checkStrictly=true 时允许）
      if (!countyName) return [cityCode]

      const countyCode = FUJIAN_COUNTY_NAME_TO_CODE_BY_CITY[cityCode]?.[countyName]
      if (!countyCode) return null

      if (!townName) return [cityCode, countyCode]

      const leafKey = FUJIAN_TOWN_NAME_TO_LEAF_KEY_BY_COUNTY[countyCode]?.[townName]
      if (!leafKey) return [cityCode, countyCode]

      return [cityCode, countyCode, leafKey]
    },

    handleChange (value) {
      if (this.multiple) {
        const raw = Array.isArray(value) ? value.map(codePath => this.codePathToText(codePath)).filter(Boolean) : []
        const seen = new Set()
        const distAreaArr = raw.filter((t) => {
          if (seen.has(t)) return false
          seen.add(t)
          return true
        })
        try { this._lastEmittedValueKey = JSON.stringify(distAreaArr) } catch (e) { this._lastEmittedValueKey = null }
        this.$emit('input', distAreaArr)
        return
      }

      const area = this.codePathToText(value)
      try { this._lastEmittedValueKey = JSON.stringify(area) } catch (e) { this._lastEmittedValueKey = null }
      this.$emit('input', area)
    },
  },
};
</script>

<style>
/* 修复：ElementUI cascader 在多选场景下会给同列节点加上 in-active-path，
   主题默认把它渲染成“选中高亮”，导致“同一区下其他镇也高亮”。这里只针对本组件的下拉面板收敛。 */
.china-area-cascader-popper .el-cascader-node.in-active-path {
  color: #606266;
  font-weight: normal;
}

.el-radio:focus:not(.is-focus):not(:active):not(.is-disabled) .el-radio__inner {
  box-shadow: none;
}
</style>

